import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import {
  Activity,
  Archive,
  BookOpen,
  CheckCircle2,
  FileText,
  History,
  Plus,
  RefreshCw,
  Search,
  Ticket,
  Wallet,
  X,
} from "lucide-react";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { parseLotteryScanCode } from "../../../../utils/lotteryScanParser";
import LotteryOperationsTicketScan from "./LotteryOperationsTicketScan";

const todayInput = () => new Date().toISOString().split("T")[0];

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.games)) return value.games;
  return [];
};

const money = (value) => {
  const number = Number(value || 0);
  return number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const ticketValue = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const parseBookScan = (value) => {
  const parsed = parseLotteryScanCode(value);
  return {
    gameNo: parsed.gameNo,
    bookNo: parsed.bookNo,
    ticketNo: parsed.ticketNo,
  };
};

const Modal = ({ title, subtitle, children, footer, onClose }) => (
  <div className="lotops-modal-overlay" role="dialog" aria-modal="true">
    <div className="lotops-modal">
      <div className="lotops-modal-head">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <button type="button" className="lotops-icon-btn" onClick={onClose} aria-label="Close modal">
          <X size={18} />
        </button>
      </div>
      <div className="lotops-modal-body">{children}</div>
      {footer && <div className="lotops-modal-footer">{footer}</div>}
    </div>
  </div>
);

const DataTable = ({ title, subtitle, columns, rows, emptyText = "No records found" }) => (
  <div className="lotops-table-card">
    <div className="lotops-section-head">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      <span className="lotops-count-pill">{rows.length}</span>
    </div>
    <div className="lotops-table-wrap">
      <table className="lotops-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="lotops-empty">
                {emptyText}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={row.id || `${title}-${index}`}>
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.render ? column.render(row, index) : row[column.key] || "-"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

function LotteryOperations() {
  const { user } = useContext(AuthContext);
  const { Post, Put, GetPaginatedData, _Get } = useApi();
  const storeId = user?.store;

  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [games, setGames] = useState([]);
  const [books, setBooks] = useState([]);
  const [movements, setMovements] = useState([]);
  const [weeklySettlements, setWeeklySettlements] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const isLoadingRef = useRef(false);

  const loadData = async () => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setLoading(true);
      const [gameResponse, bookResponse, movementResponse, weeklyResponse] = await Promise.all([
        GetPaginatedData("getMyGames", { page: 1, queryParams: { page_size: 100 } }),
        GetPaginatedData("lotteryInventoryData", { page: 1, queryParams: { page_size: 200 } }),
        GetPaginatedData("getBookMovement", { page: 1, queryParams: { page_size: 100 } }),
        GetPaginatedData("weeklyInvoiceSettlementData", { page: 1, queryParams: { page_size: 100 } }),
      ]);

      setGames(asArray(gameResponse?.data));
      setBooks(asArray(bookResponse?.data));
      setMovements(asArray(movementResponse?.data));
      setWeeklySettlements(asArray(weeklyResponse?.data));
    } catch (error) {
      console.error("Unable to load lottery operations:", error);
      toast.error("Unable to load lottery operations.");
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // Run once on mount. useApi returns fresh function identities on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dashboard = useMemo(() => {
    const activated = books.filter((book) => book.status === "activated");
    const nonActivated = books.filter((book) => book.status === "non activated");
    const settled = books.filter((book) => book.status === "settled");
    const scanned = books.filter(
      (book) => book.active_date || Number(book.quantity_sold || 0) > 0 || book.status === "activated",
    );
    const activeValue = activated.reduce((sum, book) => sum + Number(book.game_total_price || book.total || 0), 0);

    return {
      games: games.length,
      activated: activated.length,
      nonActivated: nonActivated.length,
      settled: settled.length,
      scanned: scanned.length,
      activeValue,
    };
  }, [books, games]);

  const filteredBooks = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return books;

    return books.filter((book) =>
      [
        book.game_no,
        book.game_name,
        book.book_no,
        book.status,
        book.scan_no,
        book.active_date,
        book.instant_settlement_date,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [books, search]);

  const openModal = (type, seed = {}) => {
    const defaults = {
      addGame: {
        game_name: "",
        game_no: "",
        total_no_of_tickets: "",
        value: "",
        ticket_sale_mode: "descending",
        pending_book_no: "",
        pending_scan_no: "",
      },
      receiveBook: {
        inventory_date: todayInput(),
        lottery_game: "",
        book_no: "",
        scan_no: "",
      },
      activateBook: {
        active_date: todayInput(),
        game_no: seed.game_no || "",
        book_no: seed.book_no || "",
      },
      settleBook: {
        instant_settlement_date: todayInput(),
        game_no: seed.game_no || "",
        book_no: seed.book_no || "",
        total: seed.game_total_price || seed.total || "",
        scan_no: "",
      },
      weeklySettlement: {
        week_starting: todayInput(),
        net_sales: "",
        cashes: "",
        claim_bonus: "",
        adjustment61: "",
        service_fee: "",
        bonding_fee: "",
        instant_due: "",
        total_sales: "",
        total_return: "",
        sr_31_total: "",
        adjustment38: "",
      },
    };

    setForm({ ...(defaults[type] || {}), ...seed });
    setModal(type);
  };

  const closeModal = () => {
    setModal(null);
    setForm({});
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const selectedGame = useMemo(
    () => games.find((game) => String(game.id) === String(form.lottery_game)),
    [form.lottery_game, games],
  );

  useEffect(() => {
    if (modal === "receiveBook" && selectedGame) {
      setForm((prev) => ({
        ...prev,
        game_no: selectedGame.game_no,
        game_name: selectedGame.game_name,
      }));
    }
  }, [modal, selectedGame]);

  const handleScanToForm = (value, target = "settleBook") => {
    const parsed = parseBookScan(value);
    setForm((prev) => ({
      ...prev,
      scan_no: value,
      game_no: parsed.gameNo || prev.game_no,
      book_no: parsed.bookNo || prev.book_no,
    }));
    if (target === "receiveBook") {
      const game = games.find((item) => String(item.game_no) === String(parsed.gameNo));
      if (game) {
        setForm((prev) => ({ ...prev, lottery_game: game.id, game_name: game.game_name }));
      }
    }
  };

  const handleAddGame = async (event) => {
    event.preventDefault();
    try {
      const response = await Post("lotteryGameData", {
        game_name: form.game_name,
        game_no: form.game_no,
        total_no_of_tickets: form.total_no_of_tickets,
        value: form.value,
        ticket_sale_mode: form.ticket_sale_mode || "descending",
        store: storeId,
        receive_date: todayInput(),
      });
      const createdGame = response?.data?.lotteryGame || response?.data?.data || response?.data || {};
      toast.success("Game added successfully.");
      await loadData();

      if (form.pending_book_no) {
        openModal("receiveBook", {
          inventory_date: todayInput(),
          lottery_game: createdGame.id || "",
          game_no: createdGame.game_no || form.game_no,
          game_name: createdGame.game_name || form.game_name,
          book_no: form.pending_book_no,
          scan_no: form.pending_scan_no || `${form.game_no}${form.pending_book_no}`,
        });
      } else {
        closeModal();
      }
    } catch (error) {
      toast.error(error?.response?.data?.game_no?.[0] || "Unable to add game.");
    }
  };

  const handleReceiveBook = async (event) => {
    event.preventDefault();
    if (!selectedGame && !form.lottery_game) {
      toast.error("Please select a game.");
      return;
    }

    try {
      await Post("lotteryInventoryData", {
        inventory_date: form.inventory_date,
        lottery_game: form.lottery_game,
        book_no: form.book_no,
        game_name: selectedGame?.game_name || form.game_name,
        store: storeId,
        scan_no: form.scan_no || `${selectedGame?.game_no || form.game_no}-${form.book_no}`,
      });
      toast.success("Book received into inventory.");
      closeModal();
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to receive book.");
    }
  };

  const handleActivateBook = async (event) => {
    event.preventDefault();
    try {
      const shift = await _Get("isShiftActive");
      await Post("updateGameAndInventory", {
        game_no: form.game_no,
        book_no: form.book_no,
        active_date: form.active_date,
        status: "activated",
        activated_book_shift: shift?.shift_id,
      });
      toast.success("Book activated.");
      closeModal();
      loadData();
    } catch (error) {
      toast.error("Unable to activate book. Please make sure a shift is active.");
    }
  };

  const handleSettleBook = async (event) => {
    event.preventDefault();
    try {
      await Post("updateGameAndInventory", {
        instant_settlement_date: form.instant_settlement_date,
        book_no: form.book_no,
        game_no: form.game_no,
        total: form.total,
        status: "settled",
      });
      toast.success("Book settled.");
      closeModal();
      loadData();
    } catch (error) {
      toast.error("Unable to settle book.");
    }
  };

  const weeklyTotals = useMemo(() => {
    const netSales = Number(form.net_sales || 0);
    const cashes = Number(form.cashes || 0);
    const claimBonus = Number(form.claim_bonus || 0);
    const adjustment61 = Number(form.adjustment61 || 0);
    const serviceFee = Number(form.service_fee || 0);
    const bondingFee = Number(form.bonding_fee || 0);
    const instantDue = Number(form.instant_due || 0);
    const totalSales = Number(form.total_sales || 0);
    const totalReturn = Number(form.total_return || 0);
    const sr31Total = Number(form.sr_31_total || 0);
    const adjustment38 = Number(form.adjustment38 || 0);

    const commission61 = netSales / 20;
    const cashBonus = cashes / 100;
    const onlineDue = netSales - commission61 - cashes - cashBonus - claimBonus + adjustment61 + serviceFee + bondingFee;
    const netDue = onlineDue + instantDue;
    const commission38 = (totalSales - totalReturn) / 20;
    const cashingCommission = sr31Total * 0.01;
    const totalInstantCashing = sr31Total > 0 ? sr31Total - cashingCommission : 0;
    const amountDue = totalSales - totalReturn - commission38 - sr31Total + adjustment38;

    return {
      commission61,
      cashBonus,
      onlineDue,
      netDue,
      commission38,
      cashingCommission,
      totalInstantCashing,
      amountDue,
      totalNetSales: netSales + totalSales,
      totalCashes: cashes + totalInstantCashing,
      totalCommission: commission61 + cashBonus + claimBonus + commission38 + cashingCommission,
      totalDue: netDue,
    };
  }, [form]);

  const handleWeeklySettlement = async (event) => {
    event.preventDefault();
    const startDate = new Date(form.week_starting);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    try {
      await Post("weeklyInvoiceSettlementData", {
        store: storeId,
        week_starting: form.week_starting,
        week_ending: endDate.toISOString().split("T")[0],
        net_sales: Number(form.net_sales || 0).toFixed(2),
        commission_sr_61: weeklyTotals.commission61.toFixed(2),
        cashes: Number(form.cashes || 0).toFixed(2),
        cash_bonus: weeklyTotals.cashBonus.toFixed(2),
        claim_bonus: Number(form.claim_bonus || 0).toFixed(2),
        adjustment61: Number(form.adjustment61 || 0).toFixed(2),
        service_fee: Number(form.service_fee || 0).toFixed(2),
        bonding_fee: Number(form.bonding_fee || 0).toFixed(2),
        online_due: weeklyTotals.onlineDue.toFixed(2),
        instant_due: Number(form.instant_due || 0).toFixed(2),
        net_due: weeklyTotals.netDue.toFixed(2),
        total_sales: Number(form.total_sales || 0).toFixed(2),
        total_return: Number(form.total_return || 0).toFixed(2),
        commission_sr_38: weeklyTotals.commission38.toFixed(2),
        sr_31_total: Number(form.sr_31_total || 0).toFixed(2),
        cashing_commission: weeklyTotals.cashingCommission.toFixed(2),
        total_instant_cashing: weeklyTotals.totalInstantCashing.toFixed(2),
        amount_due: weeklyTotals.amountDue.toFixed(2),
        adjustment38: Number(form.adjustment38 || 0).toFixed(2),
        total_net_sales: weeklyTotals.totalNetSales.toFixed(2),
        total_cashes: weeklyTotals.totalCashes.toFixed(2),
        total_commission: weeklyTotals.totalCommission.toFixed(2),
        total_due: weeklyTotals.totalDue.toFixed(2),
      });
      toast.success("Weekly settlement saved.");
      closeModal();
      loadData();
    } catch (error) {
      toast.error("Unable to save weekly settlement.");
    }
  };

  const nonActivatedBooks = filteredBooks.filter((book) => book.status === "non activated");
  const activatedBooks = filteredBooks.filter((book) => book.status === "activated");
  const settledBooks = filteredBooks.filter((book) => book.status === "settled");
  const scannedTickets = filteredBooks.filter((book) => book.scan_no && book.scan_no !== "activated");
  const scannedTicketRows = useMemo(() => {
    return filteredBooks
      .filter((book) => book.active_date || Number(book.quantity_sold || 0) > 0 || book.status === "activated")
      .sort((a, b) => {
        const dateCompare = String(b.active_date || b.inventory_date || "").localeCompare(String(a.active_date || a.inventory_date || ""));
        if (dateCompare !== 0) return dateCompare;
        return Number(b.quantity_sold || 0) - Number(a.quantity_sold || 0);
      });
  }, [filteredBooks]);

  const renderField = (label, field, type = "text", props = {}) => (
    <label className="lotops-field">
      <span>{label}</span>
      <input
        type={type}
        value={form[field] || ""}
        onChange={(event) => setField(field, event.target.value)}
        {...props}
      />
    </label>
  );

  const renderModal = () => {
    if (modal === "addGame") {
      return (
        <Modal
          title="Add Lottery Game"
          subtitle="Create the game once, then receive books against it."
          onClose={closeModal}
          footer={<button className="lotops-primary-btn" form="lotops-add-game">Save Game</button>}
        >
          <form id="lotops-add-game" className="lotops-form-grid" onSubmit={handleAddGame}>
            {form.pending_book_no && (
              <div className="lotops-calc-box lotops-span-2">
                <span>Scanned book waiting to receive</span>
                <strong>
                  Game {form.game_no || "-"} / Book {form.pending_book_no}
                </strong>
              </div>
            )}
            {renderField("Game Name", "game_name", "text", { required: true })}
            {renderField("Game Number", "game_no", "text", { required: true })}
            {renderField("Total Tickets", "total_no_of_tickets", "number", { required: true })}
            {renderField("Ticket Value", "value", "number", { required: true, step: "0.01" })}
            <label className="lotops-field lotops-span-2">
              <span>Ticket Sale Mode</span>
              <select
                value={form.ticket_sale_mode || "descending"}
                onChange={(event) => setField("ticket_sale_mode", event.target.value)}
                required
              >
                <option value="descending">Descending: highest ticket to 0</option>
                <option value="ascending">Ascending: 0 to highest ticket</option>
                <option value="random">Random: any unsold ticket</option>
              </select>
            </label>
          </form>
        </Modal>
      );
    }

    if (modal === "receiveBook") {
      return (
        <Modal
          title="Receive Book"
          subtitle="Add a new book to inventory before activation or ticket sales."
          onClose={closeModal}
          footer={<button className="lotops-primary-btn" form="lotops-receive-book">Receive Book</button>}
        >
          <form id="lotops-receive-book" className="lotops-form-grid" onSubmit={handleReceiveBook}>
            <label className="lotops-field lotops-span-2">
              <span>Scan or type barcode</span>
              <input
                value={form.scan_no || ""}
                onChange={(event) => handleScanToForm(event.target.value, "receiveBook")}
                placeholder="Optional: scan full book/ticket barcode"
              />
            </label>
            <label className="lotops-field">
              <span>Game</span>
              <select
                value={form.lottery_game || ""}
                onChange={(event) => setField("lottery_game", event.target.value)}
                required
              >
                <option value="">Select game</option>
                {games.map((game) => (
                  <option key={game.id} value={game.id}>
                    {game.game_no} - {game.game_name}
                  </option>
                ))}
              </select>
            </label>
            {renderField("Book Number", "book_no", "text", { required: true })}
            {renderField("Receive Date", "inventory_date", "date", { required: true })}
          </form>
        </Modal>
      );
    }

    if (modal === "activateBook") {
      return (
        <Modal
          title="Activate Book"
          subtitle="Move a received book into active selling status."
          onClose={closeModal}
          footer={<button className="lotops-primary-btn" form="lotops-activate-book">Activate Book</button>}
        >
          <form id="lotops-activate-book" className="lotops-form-grid" onSubmit={handleActivateBook}>
            {renderField("Game Number", "game_no", "text", { required: true })}
            {renderField("Book Number", "book_no", "text", { required: true })}
            {renderField("Active Date", "active_date", "date", { required: true })}
          </form>
        </Modal>
      );
    }

    if (modal === "settleBook") {
      return (
        <Modal
          title="Settle Book"
          subtitle="Close a book and record instant settlement amount."
          onClose={closeModal}
          footer={<button className="lotops-primary-btn" form="lotops-settle-book">Settle Book</button>}
        >
          <form id="lotops-settle-book" className="lotops-form-grid" onSubmit={handleSettleBook}>
            <label className="lotops-field lotops-span-2">
              <span>Scan or type barcode</span>
              <input
                value={form.scan_no || ""}
                onChange={(event) => handleScanToForm(event.target.value, "settleBook")}
                placeholder="Optional: scan book/ticket barcode"
              />
            </label>
            {renderField("Game Number", "game_no", "text", { required: true })}
            {renderField("Book Number", "book_no", "text", { required: true })}
            {renderField("Settlement Date", "instant_settlement_date", "date", { required: true })}
            {renderField("Total Sales", "total", "number", { required: true, step: "0.01" })}
          </form>
        </Modal>
      );
    }

    if (modal === "weeklySettlement") {
      return (
        <Modal
          title="Weekly Settlement"
          subtitle="Enter SR-61 and SR-38 numbers. Calculated values are shown before saving."
          onClose={closeModal}
          footer={<button className="lotops-primary-btn" form="lotops-weekly">Save Weekly Settlement</button>}
        >
          <form id="lotops-weekly" className="lotops-form-grid" onSubmit={handleWeeklySettlement}>
            {renderField("Week Starting", "week_starting", "date", { required: true })}
            <div className="lotops-calc-box">
              <span>Week Ending</span>
              <strong>
                {form.week_starting
                  ? (() => {
                      const endDate = new Date(form.week_starting);
                      endDate.setDate(endDate.getDate() + 6);
                      return endDate.toISOString().split("T")[0];
                    })()
                  : "-"}
              </strong>
            </div>
            <div className="lotops-subtitle-row lotops-span-2">SR-61 Online Settlement</div>
            {renderField("Net Sales", "net_sales", "number", { step: "0.01" })}
            {renderField("Cashes", "cashes", "number", { step: "0.01" })}
            {renderField("Claim Bonus", "claim_bonus", "number", { step: "0.01" })}
            {renderField("Adjustment", "adjustment61", "number", { step: "0.01" })}
            {renderField("Service Fee", "service_fee", "number", { step: "0.01" })}
            {renderField("Bonding Fee", "bonding_fee", "number", { step: "0.01" })}
            {renderField("Instant Due", "instant_due", "number", { step: "0.01" })}
            <div className="lotops-calc-box"><span>Net Due</span><strong>${money(weeklyTotals.netDue)}</strong></div>
            <div className="lotops-subtitle-row lotops-span-2">SR-38 Instant Settlement</div>
            {renderField("Total Sales", "total_sales", "number", { step: "0.01" })}
            {renderField("Total Return", "total_return", "number", { step: "0.01" })}
            {renderField("SR-31 Total", "sr_31_total", "number", { step: "0.01" })}
            {renderField("Adjustment", "adjustment38", "number", { step: "0.01" })}
            <div className="lotops-calc-box"><span>Amount Due</span><strong>${money(weeklyTotals.amountDue)}</strong></div>
            <div className="lotops-calc-box"><span>Total Commission</span><strong>${money(weeklyTotals.totalCommission)}</strong></div>
          </form>
        </Modal>
      );
    }

    return null;
  };

  const tabs = [
    { key: "dashboard", label: "Overview", icon: Activity },
    { key: "games", label: "Games", icon: Ticket },
    { key: "books", label: "Books", icon: BookOpen },
    { key: "settlements", label: "Settlements", icon: Wallet },
    { key: "history", label: "History", icon: History },
  ];

  return (
    <div className="responsive-box lotops-page">
      <ToastContainer />
      <style>{styles}</style>

      <div className="lotops-hero">
        <div>
          <div className="lotops-badge">Lottery Command Center</div>
          <h1>Lottery Operations</h1>
          <p>
            One place to add games, receive and activate books, scan tickets, settle books and review history.
          </p>
        </div>
        <div className="lotops-actions">
          <button type="button" className="lotops-light-btn" onClick={loadData} disabled={loading}>
            <RefreshCw size={16} />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button type="button" className="lotops-primary-btn" onClick={() => openModal("receiveBook")}>
            <Plus size={16} />
            Receive Book
          </button>
        </div>
      </div>

      <div className="lotops-quick-grid">
        <button type="button" onClick={() => openModal("addGame")} className="lotops-quick-card">
          <Ticket />
          <strong>Add Game</strong>
          <span>Create game number, name, tickets and ticket value.</span>
        </button>
        <button type="button" onClick={() => openModal("receiveBook")} className="lotops-quick-card">
          <Archive />
          <strong>Receive Book</strong>
          <span>Add book inventory from barcode or manual entry.</span>
        </button>
        <button type="button" onClick={() => openModal("activateBook")} className="lotops-quick-card">
          <CheckCircle2 />
          <strong>Activate Book</strong>
          <span>Start selling a received book during active shift.</span>
        </button>
        <button type="button" onClick={() => openModal("settleBook")} className="lotops-quick-card">
          <Wallet />
          <strong>Settle Book</strong>
          <span>Close a book and record settlement total.</span>
        </button>
        <button type="button" onClick={() => openModal("weeklySettlement")} className="lotops-quick-card">
          <FileText />
          <strong>Weekly Settlement</strong>
          <span>Record SR-61 and SR-38 weekly invoice totals.</span>
        </button>
      </div>

      <LotteryOperationsTicketScan
        className="lotops-scan-box"
        games={games}
        books={books}
        onDataUpdate={loadData}
        onAddGame={(seed) => openModal("addGame", seed)}
        onReceiveBook={(seed) => openModal("receiveBook", seed)}
        onActivateBook={(seed) => openModal("activateBook", seed)}
      />

      <div className="lotops-tabbar">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              type="button"
              key={tab.key}
              className={activeTab === tab.key ? "active" : ""}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="lotops-search-row">
        <Search size={16} />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search game, book, status, date..."
        />
      </div>

      {activeTab === "dashboard" && (
        <>
          <div className="lotops-metric-grid">
            <div className="lotops-metric"><span>Games</span><strong>{dashboard.games}</strong></div>
            <div className="lotops-metric green"><span>Active Books</span><strong>{dashboard.activated}</strong></div>
            <div className="lotops-metric amber"><span>Waiting Books</span><strong>{dashboard.nonActivated}</strong></div>
            <div className="lotops-metric blue"><span>Scanned Tickets</span><strong>{dashboard.scanned}</strong></div>
            <div className="lotops-metric purple"><span>Settled Books</span><strong>{dashboard.settled}</strong></div>
            <div className="lotops-metric dark"><span>Active Value</span><strong>${money(dashboard.activeValue)}</strong></div>
          </div>
          <DataTable
            title="Scanned Tickets"
            subtitle="Active books and the latest ticket position after scans."
            columns={[
              { key: "game_price", label: "Game Price", render: (book) => ticketValue(book.game_price ?? book.value) },
              { key: "game_name", label: "Game Name" },
              { key: "game_no", label: "Game Number" },
              { key: "book_no", label: "Book Number" },
              { key: "active_date", label: "Activated On" },
              { key: "last_ticket_no", label: "Last Ticket No", render: (book) => ticketValue(book.last_ticket_no) },
              { key: "today_ticket_no", label: "Today Ticket No", render: (book) => ticketValue(book.today_ticket_no) },
              { key: "quantity_sold", label: "Quantity Sold", render: (book) => ticketValue(book.quantity_sold) },
              { key: "total", label: "Total", render: (book) => ticketValue(book.total ?? book.game_total_price) },
            ]}
            rows={scannedTicketRows}
          />
          <DataTable
            title="Action Needed"
            subtitle="Books received but not activated."
            columns={[
              { key: "game_no", label: "Game" },
              { key: "game_name", label: "Name" },
              { key: "book_no", label: "Book" },
              { key: "game_price", label: "Price" },
              {
                key: "action",
                label: "Action",
                render: (book) => (
                  <button type="button" className="lotops-row-btn" onClick={() => openModal("activateBook", book)}>
                    Activate
                  </button>
                ),
              },
            ]}
            rows={nonActivatedBooks.slice(0, 8)}
          />
        </>
      )}

      {activeTab === "games" && (
        <DataTable
          title="Game Library"
          subtitle="Games available for book receiving."
          columns={[
            { key: "game_no", label: "Game No" },
            { key: "game_name", label: "Game Name" },
            { key: "total_no_of_tickets", label: "Tickets" },
            { key: "value", label: "Ticket Value", render: (game) => `$${money(game.value)}` },
            { key: "ticket_sale_mode", label: "Sale Mode", render: (game) => game.ticket_sale_mode || "descending" },
          ]}
          rows={games}
        />
      )}

      {activeTab === "books" && (
        <div className="lotops-grid-2">
          <DataTable
            title="Active Books"
            subtitle="Currently selling."
            columns={[
              { key: "game_no", label: "Game" },
              { key: "book_no", label: "Book" },
              { key: "active_date", label: "Activated" },
              {
                key: "action",
                label: "Action",
                render: (book) => (
                  <button type="button" className="lotops-row-btn" onClick={() => openModal("settleBook", book)}>
                    Settle
                  </button>
                ),
              },
            ]}
            rows={activatedBooks}
          />
          <DataTable
            title="Waiting Books"
            subtitle="Received but not active."
            columns={[
              { key: "inventory_date", label: "Received" },
              { key: "game_no", label: "Game" },
              { key: "book_no", label: "Book" },
              {
                key: "action",
                label: "Action",
                render: (book) => (
                  <button type="button" className="lotops-row-btn" onClick={() => openModal("activateBook", book)}>
                    Activate
                  </button>
                ),
              },
            ]}
            rows={nonActivatedBooks}
          />
        </div>
      )}

      {activeTab === "settlements" && (
        <div className="lotops-grid-2">
          <DataTable
            title="Weekly Settlements"
            subtitle="SR-61 and SR-38 invoices."
            columns={[
              { key: "week_ending", label: "Week Ending" },
              { key: "total_net_sales", label: "Net Sales", render: (row) => `$${money(row.total_net_sales)}` },
              { key: "total_commission", label: "Commission", render: (row) => `$${money(row.total_commission)}` },
              { key: "total_due", label: "Total Due", render: (row) => `$${money(row.total_due)}` },
            ]}
            rows={weeklySettlements}
          />
          <DataTable
            title="Settled Books"
            subtitle="Instant settlement history."
            columns={[
              { key: "instant_settlement_date", label: "Date" },
              { key: "game_no", label: "Game" },
              { key: "book_no", label: "Book" },
              { key: "game_total_price", label: "Total", render: (row) => `$${money(row.game_total_price || row.total)}` },
            ]}
            rows={settledBooks}
          />
        </div>
      )}

      {activeTab === "history" && (
        <DataTable
          title="Book Movement & Ticket History"
          subtitle="Audit trail for received, activated, scanned and settled books."
          columns={[
            { key: "inventory_date", label: "Inventory Date" },
            { key: "game_no", label: "Game" },
            { key: "game_name", label: "Name" },
            { key: "book_no", label: "Book" },
            { key: "status", label: "Status", render: (row) => <span className={`lotops-status ${row.status || "other"}`}>{row.status || "-"}</span> },
            { key: "scan_no", label: "Scan" },
            { key: "active_date", label: "Active Date" },
            { key: "instant_settlement_date", label: "Settled Date" },
          ]}
          rows={filteredBooks.length ? filteredBooks : movements}
        />
      )}

      {renderModal()}
    </div>
  );
}

const styles = `
  .lotops-page {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    min-height: 100vh;
    padding-bottom: 28px;
  }

  .lotops-hero {
    display: flex;
    justify-content: space-between;
    gap: 18px;
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 26px;
    border-radius: 24px;
    background: linear-gradient(135deg, #111827 0%, #1f2937 56%, #14532d 100%);
    color: #fff;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
  }

  .lotops-badge {
    display: inline-flex;
    border: 1px solid rgba(255,255,255,0.24);
    background: rgba(255,255,255,0.12);
    border-radius: 999px;
    padding: 7px 12px;
    font-weight: 900;
    font-size: 12px;
    color: #bbf7d0;
    margin-bottom: 12px;
  }

  .lotops-hero h1 {
    margin: 0;
    font-size: 32px;
    font-weight: 950;
  }

  .lotops-hero p {
    margin: 8px 0 0;
    max-width: 680px;
    color: rgba(255,255,255,0.78);
    line-height: 1.5;
  }

  .lotops-actions,
  .lotops-tabbar,
  .lotops-head-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .lotops-primary-btn,
  .lotops-light-btn,
  .lotops-row-btn {
    border: none;
    border-radius: 999px;
    padding: 10px 15px;
    font-weight: 900;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
  }

  .lotops-primary-btn {
    background: #22c55e;
    color: #052e16;
  }

  .lotops-light-btn {
    background: #fff;
    color: #111827;
  }

  .lotops-row-btn {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
    padding: 7px 11px;
    font-size: 12px;
  }

  .lotops-quick-grid,
  .lotops-metric-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin: 16px 0;
  }

  .lotops-quick-card,
  .lotops-metric,
  .lotops-table-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 18px;
    box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
  }

  .lotops-quick-card {
    text-align: left;
    padding: 15px;
    cursor: pointer;
    color: #111827;
  }

  .lotops-quick-card svg {
    color: #16a34a;
    margin-bottom: 8px;
  }

  .lotops-quick-card strong,
  .lotops-quick-card span {
    display: block;
  }

  .lotops-quick-card span {
    margin-top: 5px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
  }

  .lotops-scan-box {
    margin: 0 0 16px;
    border-radius: 18px;
    box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
  }

  .lotops-tabbar {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 18px;
    padding: 8px;
    margin-bottom: 12px;
  }

  .lotops-tabbar button {
    border: none;
    background: transparent;
    color: #64748b;
    border-radius: 12px;
    padding: 10px 14px;
    font-weight: 900;
    display: inline-flex;
    gap: 7px;
    align-items: center;
    cursor: pointer;
  }

  .lotops-tabbar button.active {
    background: #111827;
    color: #fff;
  }

  .lotops-search-row {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 0 14px;
    height: 44px;
    margin-bottom: 14px;
  }

  .lotops-search-row input {
    border: none;
    outline: none;
    width: 100%;
    font-weight: 800;
  }

  .lotops-metric {
    padding: 16px;
  }

  .lotops-metric span {
    color: #64748b;
    font-size: 12px;
    font-weight: 950;
    text-transform: uppercase;
  }

  .lotops-metric strong {
    display: block;
    margin-top: 8px;
    color: #111827;
    font-size: 24px;
    font-weight: 950;
  }

  .lotops-metric.green { background: #ecfdf5; border-color: #bbf7d0; }
  .lotops-metric.amber { background: #fffbeb; border-color: #fde68a; }
  .lotops-metric.blue { background: #eff6ff; border-color: #bfdbfe; }
  .lotops-metric.purple { background: #f5f3ff; border-color: #ddd6fe; }
  .lotops-metric.dark { background: #111827; border-color: #111827; }
  .lotops-metric.dark span,
  .lotops-metric.dark strong { color: #fff; }

  .lotops-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .lotops-table-card {
    padding: 16px;
    margin-bottom: 14px;
  }

  .lotops-section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .lotops-section-head h3 {
    margin: 0;
    color: #111827;
    font-size: 18px;
    font-weight: 950;
  }

  .lotops-section-head p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .lotops-count-pill {
    background: #f1f5f9;
    color: #334155;
    border-radius: 999px;
    padding: 7px 10px;
    font-weight: 950;
  }

  .lotops-table-wrap {
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
  }

  .lotops-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
  }

  .lotops-table th {
    background: #f8fafc;
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    text-transform: uppercase;
    padding: 11px;
    text-align: left;
    white-space: nowrap;
  }

  .lotops-table td {
    border-top: 1px solid #f1f5f9;
    padding: 11px;
    color: #111827;
    font-size: 13px;
    font-weight: 750;
    white-space: nowrap;
  }

  .lotops-empty {
    color: #94a3b8 !important;
    text-align: center;
    padding: 22px !important;
  }

  .lotops-status {
    border-radius: 999px;
    padding: 6px 9px;
    font-size: 11px;
    font-weight: 950;
    background: #f1f5f9;
    color: #334155;
  }

  .lotops-status.activated { background: #ecfdf5; color: #047857; }
  .lotops-status.settled { background: #eff6ff; color: #1d4ed8; }

  .lotops-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    background: rgba(15, 23, 42, 0.52);
  }

  .lotops-modal {
    width: min(760px, 100%);
    max-height: min(88vh, 780px);
    overflow: auto;
    background: #fff;
    border-radius: 22px;
    box-shadow: 0 30px 80px rgba(15, 23, 42, 0.32);
  }

  .lotops-modal-head,
  .lotops-modal-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    border-bottom: 1px solid #e5e7eb;
  }

  .lotops-modal-footer {
    border-top: 1px solid #e5e7eb;
    border-bottom: none;
    justify-content: flex-end;
  }

  .lotops-modal-head h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 950;
  }

  .lotops-modal-head p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .lotops-icon-btn {
    border: none;
    border-radius: 12px;
    background: #f1f5f9;
    color: #334155;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .lotops-modal-body {
    padding: 20px;
  }

  .lotops-form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .lotops-span-2,
  .lotops-subtitle-row {
    grid-column: 1 / -1;
  }

  .lotops-field span,
  .lotops-calc-box span {
    display: block;
    margin-bottom: 6px;
    color: #64748b;
    font-size: 12px;
    font-weight: 950;
  }

  .lotops-field input,
  .lotops-field select {
    width: 100%;
    height: 42px;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    padding: 0 12px;
    color: #111827;
    font-weight: 850;
    background: #fff;
  }

  .lotops-calc-box {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 11px 12px;
  }

  .lotops-calc-box strong {
    color: #111827;
    font-size: 15px;
  }

  .lotops-subtitle-row {
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 12px;
    background: #111827;
    color: #fff;
    font-weight: 950;
  }

  @media (max-width: 1200px) {
    .lotops-quick-grid,
    .lotops-metric-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .lotops-grid-2 {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 720px) {
    .lotops-hero {
      border-radius: 18px;
      padding: 20px;
    }
    .lotops-quick-grid,
    .lotops-metric-grid,
    .lotops-form-grid {
      grid-template-columns: 1fr;
    }
    .lotops-actions,
    .lotops-primary-btn,
    .lotops-light-btn,
    .lotops-tabbar button {
      width: 100%;
    }
  }
`;

export default LotteryOperations;
